import { useCallback, useState } from "react";
import { Backdrop, CircularProgress, Typography } from "@mui/material";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { Button, useNotify } from "react-admin";
import { candidateRequests } from "api/candidateApi";
import dataProvider from "server/dataProvider";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import { useNotifyError } from "utils/useNotifyError";
import GoogleUsersDialog from "./GoogleUsersDialog";
import { mergeBatch } from "./mergeBatch";

// Creates the Google accounts for the campaign's approved candidacies that sit a remote exam. One call
// provisions a single batch, so the admin keeps going until nothing remains. Re-running is safe: candidacies
// that already have an account are skipped by the backend.
const GenerateGoogleUsersButton = ({ campaignId, disabled }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Candidacies already known to have failed are excluded so the backend fills the batch with untried ones
  // instead of spending slots on the same failures every call.
  const postBatch = useCallback(
    async (excludeCandidacyIds = []) =>
      (
        await dataProvider.customRequest(
          candidateRequests.campaignGoogleUsers(campaignId),
          { excludeCandidacyIds },
          "POST",
        )
      ).data,
    [campaignId],
  );

  const notifyBatch = batch => {
    const parts = [`Създадени ${batch.created?.length ?? 0} акаунта.`];

    parts.push(batch.remainingCount > 0 ? `Остават ${batch.remainingCount}.` : "Няма останали кандидатури.");

    if (batch.skippedCount > 0) {
      parts.push(`Пропуснати ${batch.skippedCount} след неуспех.`);
    }

    notify(parts.join(" "), { type: batch.remainingCount > 0 ? "info" : "success" });
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const batch = await postBatch();
      setResult(mergeBatch(null, batch));
      notifyBatch(batch);
    } catch (error) {
      // Own catch because the result state must stay clean on failure; `false` keeps the modal open.
      notifyError(error);
      return false;
    } finally {
      setGenerating(false);
    }
  };

  // Runs outside ConfirmModal, so it owns its error handling and loading flag.
  const handleGenerateNext = async () => {
    setGenerating(true);
    try {
      const batch = await postBatch((result?.failed ?? []).map(row => row.candidacyId));
      setResult(previous => mergeBatch(previous, batch));
      notifyBatch(batch);
    } catch (error) {
      notifyError(error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <ConfirmModal
        titleContent="Генериране на Google акаунти"
        saveTitle="Генерирай"
        onConfirm={handleGenerate}
        triggerButton={
          <Button
            label="Генерирай Google акаунти"
            variant="contained"
            size="small"
            disabled={disabled}
          >
            <GroupAddIcon />
          </Button>
        }
      >
        <Typography sx={{ p: 2 }}>
          Ще бъдат създадени Google акаунти за одобрените кандидатури с дистанционен изпит в тази кампания, които
          още нямат акаунт. Създаването на един акаунт отнема няколко секунди, затова акаунтите се генерират на
          партиди — след всяка партида ще видите колко кандидатури остават.
        </Typography>
        <Typography
          color="error"
          fontWeight="bold"
          sx={{ px: 2, pb: 2 }}
        >
          Създадените акаунти не могат да бъдат изтрити от тук.
        </Typography>
      </ConfirmModal>

      <GoogleUsersDialog
        open={Boolean(result)}
        onClose={() => setResult(null)}
        title="Генерирани Google акаунти"
        created={result?.created ?? []}
        failed={result?.failed ?? []}
        remainingCount={result?.remainingCount ?? 0}
        skippedCount={result?.skippedCount ?? 0}
        batchSize={result?.batchSize ?? 0}
        onGenerateNext={handleGenerateNext}
        generating={generating}
      />

      {/* Owned here rather than in the dialog: the first batch runs from ConfirmModal, which only disables its
          buttons, and the results dialog does not exist yet at that point. */}
      <Backdrop
        sx={{ zIndex: 10000 }}
        open={generating}
      >
        <CircularProgress
          color="primary"
          size={60}
        />
      </Backdrop>
    </>
  );
};

export default GenerateGoogleUsersButton;
